import { useTranslation } from "react-i18next";
import { useRootSelector } from "store";
import { AccountInfo } from "store/account/account-state";
import { User } from "store/user/user";
import { renderConditionalProp } from "utility";

import classes from "./ReadOnlyCreate.module.scss";

interface ReadOnlyCreateProps {
  user: User;
  editHandler: Function;
  accountInfo: AccountInfo;
}

export const ReadOnlyCreate: React.FC<ReadOnlyCreateProps> = ({
  user,
  editHandler,
  accountInfo,
}) => {
  const { t } = useTranslation([
    "account_next",
    "common",
  ]);
  const { paymentCenter, applicationName, notifications } = useRootSelector((state) => state.config);

  const eStatementEnableFlag = !!(
    user?.preferences.notifications.templatePreferences?.eStatementAvailable
      ?.sendEmail ||
    user?.preferences.notifications.templatePreferences?.eStatementAvailable
      ?.sendSms
  );

  const renderEStatementControls = () => {
    return (
      <div className={classes.EStatements}>
        <ft-divider></ft-divider>
        <h3>{t("account_next:eStatement_control_title")}</h3>
        <div key={"eStatements_toggle"} className={classes.Fields}>
          <div className={classes.ToggleContainer}>
            <h5>
              {t("account_next:eStatements")} (
              {eStatementEnableFlag
                ? t("account_next:enrolled")
                : t("account_next:notenrolled")}
              )
            </h5>
            <ft-switch
              aria-label="eStatements switch button"
              {...renderConditionalProp(
                !!(
                  user?.preferences.notifications.templatePreferences
                    ?.eStatementAvailable?.sendEmail ||
                  user?.preferences.notifications.templatePreferences
                    ?.eStatementAvailable?.sendSms
                ),
                "selected"
              )}
              disabled
            ></ft-switch>
          </div>
          <p className={classes.EStatementsControlText}>
            {t("account_next:eStatement_enroll_text")}
          </p>
        </div>
        {true && (
          <div key={"eStatements_checkbox"} className={classes.Fields}>
            <h5>{t("account_next:eStatement_available")}</h5>
            <ft-formfield label={t("account_next:email_label")}>
              <ft-checkbox
                {...renderConditionalProp(
                  !!user?.preferences.notifications.templatePreferences
                    ?.eStatementAvailable?.sendEmail,
                  "checked"
                )}
                disabled
              ></ft-checkbox>
            </ft-formfield>
            <ft-formfield label={t("account_next:text_sms")}>
              <ft-checkbox
                {...renderConditionalProp(
                  !!user?.preferences.notifications.templatePreferences
                    ?.eStatementAvailable?.sendSms,
                  "checked"
                )}
                disabled
              ></ft-checkbox>
            </ft-formfield>
          </div>
        )}
      </div>
    );
  };

  const renderNotificationFields = () => {
    return (
      <>
        {paymentCenter.notifications.sendPaymentReminder ? (
          <div key={"payment_reminder_scheduled"} className={classes.Fields}>
            <h5>
              {t("account_next:payment_reminder_scheduled", {
                day: paymentCenter.notifications
                  .daysBeforeDueDateToSendPaymentDueReminder,
              })}
            </h5>
            {notifications.enableEmail &&
            <ft-formfield label={t("account_next:email_label")}>
              <ft-checkbox
                {...renderConditionalProp(
                  !!user?.preferences.notifications.templatePreferences
                    ?.paymentReminder?.sendEmail,
                  "checked"
                )}
                disabled
              ></ft-checkbox>
            </ft-formfield>
            }
            {notifications.enableSMS &&
            <ft-formfield label={t("account_next:text_sms")}>
              <ft-checkbox
                {...renderConditionalProp(
                  !!user?.preferences.notifications.templatePreferences
                    ?.paymentReminder?.sendSms,
                  "checked"
                )}
                disabled
              ></ft-checkbox>
            </ft-formfield>
            }
          </div>
        ) : null}
        {paymentCenter.notifications.sendPastDue ? (
          <div key={"payment_past_due"} className={classes.Fields}>
            <h5>
              {t("account_next:payment_past_due", {
                day: paymentCenter.notifications.daysAfterDueDateToSendPastDue,
              })}
            </h5>
            {notifications.enableEmail &&
            <ft-formfield label={t("account_next:email_label")}>
              <ft-checkbox
                {...renderConditionalProp(
                  !!user?.preferences.notifications.templatePreferences
                    ?.pastDueReminder?.sendEmail,
                  "checked"
                )}
                disabled
              ></ft-checkbox>
            </ft-formfield>
            }
            {notifications.enableSMS &&
            <ft-formfield label={t("account_next:text_sms")}>
              <ft-checkbox
                {...renderConditionalProp(
                  !!user?.preferences.notifications.templatePreferences
                    ?.pastDueReminder?.sendSms,
                  "checked"
                )}
                disabled
              ></ft-checkbox>
            </ft-formfield>
            }
          </div>
        ) : null}
        <div key={"payment_confirmation"} className={classes.Fields}>
          <h5>{t("account_next:payment_confirmation")}</h5>
          <ft-formfield label={t("account_next:email_label")}>
            <ft-checkbox
              {...renderConditionalProp(true, "checked")}
              disabled
            ></ft-checkbox>
          </ft-formfield>
          {notifications.enableSMS &&
          <ft-formfield label={t("account_next:text_sms")}>
            <ft-checkbox
              {...renderConditionalProp(
                !!user?.preferences.notifications.templatePreferences
                  ?.paymentConfirmation?.sendSms,
                "checked"
              )}
              disabled
            ></ft-checkbox>
          </ft-formfield>
          }
        </div>
      </>
    );
  };

  const renderFields = () => {
    return [
      <div key={"first_name_label"} className={classes.Fields}>
        <label>{t("account_next:first_name_label")}</label>
        <p>{user.givenName || "--"}</p>
      </div>,
      <div key={"last_name_label"} className={classes.Fields}>
        <label>{t("account_next:last_name_label")}</label>
        <p>{user.familyName || "--"}</p>
      </div>,
      <div key={"address_line1"} className={classes.Fields}>
        <label>{t("account_next:address_line1")}</label>
        <p>{user.addressLine1 || "--"}</p>
      </div>,
      <div key={"address_line2"} className={classes.Fields}>
        <label>{t("account_next:address_line2")}</label>
        <p>{user.addressLine2 || "--"}</p>
      </div>,
      <div key={"country"} className={classes.Fields}>
        <label>{t("account_next:country")}</label>
        <p>{user.country || "--"}</p>
      </div>,
      <div key={"city"} className={classes.Fields}>
        <label>{t("account_next:city")}</label>
        <p>{user.city || "--"}</p>
      </div>,
      <div key={"state"} className={classes.Fields}>
        <label>{t("account_next:state")}</label>
        <p>{user.state || "--"}</p>
      </div>,
      <div key={"postalCode"} className={classes.Fields}>
        <label>{t("account_next:postalCode")}</label>
        <p>{user.postalCode || "--"}</p>
      </div>,
      <div key={"accountNo"} className={classes.Fields}>
        <label>{t("account_next:accountNo")}</label>
        <p>{accountInfo?.accountNumber || "--"}</p>
      </div>,
      <div key={"phone_home_business"} className={classes.Fields}>
        <label>{t("account_next:phone_home_business")}</label>
        <p>{user.phone || "--"}</p>
      </div>,
      <div key={"cell_phone"} className={classes.Fields}>
        <label>{t("account_next:cell_phone")}</label>
        <p>{user.preferences.notifications.textPhoneNumber || "--"}</p>
      </div>,
      <div key={"email_label"} className={classes.Fields}>
        <label>{t("account_next:email_label")}</label>
        <p>{user.email || "--"}</p>
      </div>,
      <div key={"company_name_label"} className={classes.Fields}>
        <label>{t("account_next:company_name_label")}</label>
        <p>{user.companyName || "--"}</p>
      </div>,
    ];
  };

  return (
    <div className={classes.Container}>
      <p className={classes.PageText}>
        {t("account_next:account_update_detail", {
          clientName: applicationName,
        })}
      </p>
      <div className={classes.EditButtonContainer}>
        <ft-button
          class={classes.EditButton}
          label={t("account_next:edit_details")}
          outlined
          onClick={editHandler}
        ></ft-button>
      </div>
      <div className={classes.FieldsWrapper}>{renderFields()}</div>
      {paymentCenter.statementsEnabled && (
        <div>{renderEStatementControls()}</div>
      )}
      {paymentCenter.notifications.sendNotifications && (notifications.enableEmail || notifications.enableSMS) ? (
        <>
          <ft-divider></ft-divider>
          <h3>{t("account_next:notification_configurations")}</h3>
          <div>{renderNotificationFields()}</div>
        </>
      ) : null}
    </div>
  );
};
